import { ChatProvider } from "@health/sdk-react";
import type { Brand } from "@health/types";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { dispatchAuthChange } from "@/lib/auth/auth-events";
import { AuthHeader } from "../components/auth/AuthHeader";
import { CategoryCard } from "../components/CategoryCard";
import { PrototypeChatSurface } from "../components/chat/PrototypeChatSurface";
import { getCategoriesForBrand } from "../data/category-display";
import { useBrandTheme } from "../hooks/useBrandTheme";
import { useCategoryTransition } from "../hooks/useCategoryTransition";

export function BrandLandingPage() {
  const { brand } = useParams({ strict: false }) as { brand: Brand };
  useBrandTheme(brand);

  const serviceUrl = import.meta.env.VITE_COMPANION_SERVICE_URL ?? "";
  const categories = getCategoriesForBrand(brand);
  const handleToolCall = useCategoryTransition(brand);
  const [explorerOpen, setExplorerOpen] = useState(false);

  return (
    <ChatProvider
      clientOptions={{ baseUrl: serviceUrl }}
      host={{
        hostApiBaseUrl: serviceUrl,
        ...(brand === "hims" || brand === "hers" ? { siteId: brand } : {}),
        onAuthChange: () => dispatchAuthChange(),
      }}
    >
      <div className="min-h-screen bg-bg">
        {/* Branded header with accent background */}
        <header className="bg-accent px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex h-11 w-11 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                aria-label="Back to brand selection"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <span className="text-2xl font-bold tracking-tight text-white">{brand}</span>
            </div>
            <AuthHeader />
          </div>
        </header>

        {/* Branded hero strip */}
        <div className="bg-accent-light px-6 py-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-semibold text-text-primary">What can we help you with?</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Choose a category or chat with our assistant below.
            </p>
          </div>
        </div>

        <main className="mx-auto max-w-3xl px-6 pb-8">
          <section aria-label="Browse categories" className="-mt-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {categories.map((cat) => (
                <CategoryCard key={cat.id} category={cat} brand={brand} />
              ))}
            </div>
          </section>

          <section aria-label="Health explorer chat" className="mt-10">
            {/* Mobile: collapsible CTA */}
            <div className="sm:hidden">
              {!explorerOpen && (
                <button
                  type="button"
                  onClick={() => setExplorerOpen(true)}
                  aria-expanded={explorerOpen}
                  className="w-full rounded-xl border border-brand-teal/30 bg-accent-light px-4 py-3 text-sm font-medium text-accent transition-colors hover:bg-accent-light/80"
                >
                  Not sure? Chat with our assistant
                </button>
              )}
              {explorerOpen && (
                <div className="flex h-[80vh] min-h-0 flex-col overflow-hidden rounded-xl border border-brand-teal/30 shadow-sm">
                  <PrototypeChatSurface
                    brand={brand}
                    category="explorer"
                    surfaceIntent="browsing"
                    onToolCall={handleToolCall}
                  />
                </div>
              )}
            </div>
            {/* Desktop: always visible */}
            <div className="hidden sm:block">
              <h3 className="mb-3 text-sm font-medium text-text-secondary">
                Not sure where to start? Ask our assistant.
              </h3>
              <div className="flex h-[80vh] min-h-0 flex-col overflow-hidden rounded-xl border border-brand-teal/30 shadow-sm">
                <PrototypeChatSurface
                  brand={brand}
                  category="explorer"
                  surfaceIntent="browsing"
                  onToolCall={handleToolCall}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
    </ChatProvider>
  );
}
